import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import "./Assets/styles/landing-v2.css";
import darkLogo from "./Assets/images/dark_logo.png";
import flagstoneLogo from "./Assets/images/flagstone_logo.png";

const sectorConfig = {
  voeding: {
    label: "Voeding & drank",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "In de voedingssector zijn omstellingen (CIP, reinigingen, allergenenwissels) doorgaans de zwaarste verliespost.",
    verlies: {
      wachten:    { low: 0.008, high: 0.018, label: "Wachten & buffers",       context: "Hygiënevensters en batchovergangen veroorzaken structurele wachttijd." },
      omstelling: { low: 0.015, high: 0.030, label: "Omstellingen & reiniging", context: "CIP-cycli, allergenenwissels en formatwissels zijn tijdsintensief en kostbaar." },
      flow:       { low: 0.008, high: 0.018, label: "Capaciteitsverlies",       context: "Suboptimale lijnbalans of onderbenutte capaciteit tussen stappen." },
      stops:      { low: 0.004, high: 0.010, label: "Kleine stops",             context: "Verstoringen door verpakkingsfouten, vulproblemen of sensorstoringen." },
    },
  },
  machinebouw: {
    label: "Machinebouw",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "In machinebouw zijn wachttijd (componenten, goedkeuringen, tooling) en omsteltijd (kleine series, hoge variatie) de dominante verliesposten.",
    verlies: {
      wachten:    { low: 0.012, high: 0.025, label: "Wachten & buffers",       context: "Wachten op onderdelen, goedkeuringen of tooling bij kleine series." },
      omstelling: { low: 0.010, high: 0.022, label: "Omstellingen & variatie",  context: "Hoge productmix en korte series maken omsteltijd relatief zwaar." },
      flow:       { low: 0.007, high: 0.016, label: "Capaciteitsverlies",       context: "Bottlenecks in de flow door ongelijke belasting van werkposten." },
      stops:      { low: 0.004, high: 0.010, label: "Kleine stops",             context: "Micro-stops door toolingproblemen, kwaliteitscontrole of materiaalwissels." },
    },
  },
  farma: {
    label: "Farma / chemie",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "In farma en chemie domineren omstellings- en validatiekosten. Elke omstelling vereist strikte documentatie, cleaning en validatiestappen.",
    verlies: {
      wachten:    { low: 0.006, high: 0.015, label: "Wachten & buffers",       context: "Wachttijd door kwaliteitscontroles, quarantaineperiodes en vrijgaveprocedures." },
      omstelling: { low: 0.012, high: 0.025, label: "Omstellingen & validatie", context: "Validatie- en documentatieverplichtingen maken elke omstelling kostbaar." },
      flow:       { low: 0.005, high: 0.012, label: "Capaciteitsverlies",       context: "Strikte procesparameters beperken de flexibiliteit in capaciteitsbenutting." },
      stops:      { low: 0.003, high: 0.008, label: "Kleine stops",             context: "Procesverstoringen met disproportionele impact door compliance-vereisten." },
    },
  },
  overig: {
    label: "Overige productie",
    omzetLabel: { 5: "€1–10M", 25: "€10–50M", 75: "€50–100M", 150: "€100M+" },
    note: "Verliesstructuur varieert sterk per omgeving. Ranges gebaseerd op gangbare OEE-benchmarks voor gemengde productieomgevingen.",
    verlies: {
      wachten:    { low: 0.008, high: 0.020, label: "Wachten & buffers",       context: "Wachttijd door interne logistiek, planning of afhankelijkheden tussen stappen." },
      omstelling: { low: 0.008, high: 0.020, label: "Omstellingen & variatie",  context: "Omsteltijd afhankelijk van productvariatie en seriegrootte." },
      flow:       { low: 0.006, high: 0.015, label: "Capaciteitsverlies",       context: "Beschikbare capaciteit geraakt niet optimaal benut door flow-onbalans." },
      stops:      { low: 0.003, high: 0.009, label: "Kleine stops",             context: "Verborgen verlies door frequente kleine verstoringen." },
    },
  },
};

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const fmt = (v) =>
  v >= 1e6
    ? `${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`
    : `${Math.round(v / 1000)}k`;

const fmtRange = (low, high) => `${fmt(low)} – ${fmt(high)}`;

function LandingPage() {
  const { t, i18n } = useTranslation();
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedOmzet, setSelectedOmzet] = useState(null);
  const [selectedVerlies, setSelectedVerlies] = useState(new Set());

  const toggleVerlies = (val) => {
    setSelectedVerlies((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const cfg = selectedSector ? sectorConfig[selectedSector] : null;
  const omzetM = selectedOmzet ? selectedOmzet * 1e6 : null;
  const showResult = !!(selectedSector && selectedOmzet && selectedVerlies.size > 0);

  const { calcLow, calcHigh } = useMemo(() => {
    if (!showResult || !cfg || !omzetM) return { calcLow: 0, calcHigh: 0 };
    let low = 0, high = 0;
    selectedVerlies.forEach((v) => {
      if (cfg.verlies[v]) {
        low += cfg.verlies[v].low;
        high += cfg.verlies[v].high;
      }
    });
    return { calcLow: omzetM * low, calcHigh: omzetM * high };
  }, [showResult, cfg, omzetM, selectedVerlies]);

  const { simTotalLow, simTotalHigh } = useMemo(() => {
    if (!cfg || !omzetM) return { simTotalLow: 0, simTotalHigh: 0 };
    const low = Object.values(cfg.verlies).reduce((s, v) => s + v.low, 0) * omzetM;
    const high = Object.values(cfg.verlies).reduce((s, v) => s + v.high, 0) * omzetM;
    return { simTotalLow: low, simTotalHigh: high };
  }, [cfg, omzetM]);

  const rangeText = showResult ? fmtRange(calcLow, calcHigh) : "—";

  const dynamicCtaHref =
    showResult && cfg
      ? `mailto:info@flagstone.tech?subject=Analyse besparingspotentieel ${cfg.label}&body=Beste Taplr, wij draaien een omzet van ${cfg.omzetLabel[selectedOmzet]} in de sector ${cfg.label} en willen graag onderzoeken hoe we onze operationele verliezen kunnen reduceren.`
      : "mailto:info@flagstone.tech?subject=Schatting besparingspotentieel";

  return (
    <div
      style={{
        fontFamily: "Inter, Segoe UI, Arial, sans-serif",
        color: "#07142d",
        background: "#ffffff",
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #e2e7ef",
        }}
      >
        <div className="lp2-container lp2-nav">
          <div className="lp2-brand">
            <img src={darkLogo} alt="Taplr" className="lp2-brand-logo" />
          </div>
          <nav className="lp2-nav-links">
            <button className="lp2-nav-anchor" onClick={() => scrollTo("problems")}>{t('landingPage.navHerkenbaar')}</button>
            <button className="lp2-nav-anchor" onClick={() => scrollTo("production-intelligence")}>{t('landingPage.navProductionIntelligence')}</button>
            <button className="lp2-nav-anchor" onClick={() => scrollTo("case")}>{t('landingPage.navSimulatie')}</button>
            <button className="lp2-nav-anchor" onClick={() => scrollTo("partner")}>{t('landingPage.navImplementatie')}</button>
            <a className="lp2-cta lp2-cta-secondary" href="mailto:info@flagstone.tech?subject=Bel me terug">
              {t('landingPage.navContactExpert')}
            </a>
            <Link className="lp2-cta lp2-cta-secondary" to="/login">
              {t('auth.login')}
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="lp2-hero">
          <div className="lp2-container lp2-hero-grid">
            <div>
              <div className="lp2-eyebrow">
                <span className="lp2-dot"></span>
                {t('landingPage.eyebrow')}
              </div>
              <h1 style={{ fontSize: "clamp(38px, 4.5vw, 62px)", lineHeight: 1.05, margin: "0 0 24px", letterSpacing: "-0.045em" }}>
                {t('landingPage.heroTitle')}
              </h1>
              <p className="lp2-lead">
                {t('landingPage.heroLead')}
              </p>
              <div className="lp2-hero-actions">
                <a className="lp2-cta lp2-cta-secondary" href="mailto:info@flagstone.tech?subject=Bel me terug">
                  {t('landingPage.heroCta')}
                </a>
                <button className="lp2-cta" onClick={() => scrollTo("case")}>{t('landingPage.heroSimulationCta')}</button>
              </div>
            </div>

            <div className="lp2-hero-card" id="hero-calculator" aria-label={t('landingPage.calcTitle')}>
              <div className="lp2-calc-label">{t('landingPage.calcTitle')}</div>
              <div className="lp2-calc-sub">{t('landingPage.calcSubtitle')}</div>

              <div className="lp2-calc-field">
                <label className="lp2-calc-field-label">{t('landingPage.calcSectorLabel')}</label>
                <div className="lp2-calc-options">
                  {[
                    { val: "voeding", label: t('landingPage.calcSectorVoeding') },
                    { val: "machinebouw", label: t('landingPage.calcSectorMachinebouw') },
                    { val: "farma", label: t('landingPage.calcSectorFarma') },
                    { val: "overig", label: t('landingPage.calcSectorOverig') },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      className={`lp2-calc-opt${selectedSector === opt.val ? " active" : ""}`}
                      aria-pressed={selectedSector === opt.val ? "true" : "false"}
                      onClick={() => setSelectedSector(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lp2-calc-field">
                <label className="lp2-calc-field-label">{t('landingPage.calcOmzetLabel')}</label>
                <div className="lp2-calc-options">
                  {[
                    { val: 5, label: "€1–10M" },
                    { val: 25, label: "€10–50M" },
                    { val: 75, label: "€50–100M" },
                    { val: 150, label: "€100M+" },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      className={`lp2-calc-opt${selectedOmzet === opt.val ? " active" : ""}`}
                      aria-pressed={selectedOmzet === opt.val ? "true" : "false"}
                      onClick={() => setSelectedOmzet(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lp2-calc-field">
                <label className="lp2-calc-field-label">
                  {t('landingPage.calcVerliesLabel')}{" "}
                  <span style={{ fontWeight: 400, color: "#657084" }}>{t('landingPage.calcVerliesMultiple')}</span>
                </label>
                <div className="lp2-calc-options lp2-flex-wrap">
                  {[
                    { val: "wachten", label: t('landingPage.calcVerliesWachten') },
                    { val: "omstelling", label: t('landingPage.calcVerliesOmstelling') },
                    { val: "flow", label: t('landingPage.calcVerliesFlow') },
                    { val: "stops", label: t('landingPage.calcVerliesStops') },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      className={`lp2-calc-opt${selectedVerlies.has(opt.val) ? " active" : ""}`}
                      aria-pressed={selectedVerlies.has(opt.val) ? "true" : "false"}
                      onClick={() => toggleVerlies(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {showResult ? (
                <div className="lp2-calc-result">
                  <div className="lp2-calc-result-label">{t('landingPage.calcResultLabel')}</div>
                  <div className="lp2-calc-result-value">{rangeText} {t('landingPage.calcResultPerYear')}</div>
                  <div className="lp2-calc-result-note">{cfg.note}</div>
                  <a className="lp2-cta lp2-cta-dynamic-calc" href={dynamicCtaHref}>
                    {t('landingPage.calcResultCta', { range: rangeText })}
                  </a>
                  <div className="lp2-calc-method-note">
                    <span className="lp2-calc-method-icon">&#8505;</span>
                    {t('landingPage.calcMethodNote')}
                    <a href="#how-we-calculate" className="lp2-calc-method-link">{t('landingPage.calcMethodLink')}</a>
                  </div>
                </div>
              ) : (
                <div className="lp2-calc-empty">{t('landingPage.calcEmpty')}</div>
              )}
            </div>
          </div>
        </section>

        <section id="problems" style={{ padding: "82px 0" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>{t('landingPage.problemsTitle')}</h2>
              <p>{t('landingPage.problemsSubtitle')}</p>
            </div>
            <div className="lp2-pain-grid">
              {[
                { n: "01", label: t('landingPage.pain01') },
                { n: "02", label: t('landingPage.pain02') },
                { n: "03", label: t('landingPage.pain03') },
                { n: "04", label: t('landingPage.pain04') },
                { n: "05", label: t('landingPage.pain05') },
                { n: "06", label: t('landingPage.pain06') },
              ].map((item) => (
                <div key={item.n} className="lp2-pain-card">
                  <div>
                    <div className="lp2-pain-number">{item.n}</div>
                    <h3>{item.label}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lp2-intelligence" id="production-intelligence">
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>{t('landingPage.intelligenceTitle')}</h2>
              <p>
                {t('landingPage.intelligenceSubtitle')}
              </p>
            </div>
            <div className="lp2-flow">
              {[
                { n: "1", label: t('landingPage.flowStep1') },
                { n: "2", label: t('landingPage.flowStep2') },
                { n: "3", label: t('landingPage.flowStep3') },
                { n: "4", label: t('landingPage.flowStep4') },
              ].map((step) => (
                <div key={step.n} className="lp2-flow-step">
                  <span>{step.n}</span>
                  <h3>{step.label}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="case" style={{ padding: "82px 0" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              {selectedSector && omzetM && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 999, background: "#fff4e8", color: "#9b5208", fontWeight: 700, fontSize: 13 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f28c1b", display: "inline-block" }}></span>
                    {cfg.label} &middot; {cfg.omzetLabel[selectedOmzet]}
                  </span>
                </div>
              )}
              <h2>
                {selectedSector && omzetM
                  ? t('landingPage.caseDynamicTitle', { sector: cfg.label })
                  : t('landingPage.caseDefaultTitle')}
              </h2>
              <p>
                {selectedSector && omzetM
                  ? t('landingPage.caseDynamicSubtitle')
                  : t('landingPage.caseDefaultSubtitle')}
              </p>
              {!(selectedSector && omzetM) && (
                <button onClick={() => scrollTo("hero-calculator")} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 12, padding: "10px 18px", borderRadius: 999, background: "#f28c1b", color: "white", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                  {t('landingPage.caseFillIn')}
                </button>
              )}
            </div>

            <div className="lp2-case-grid">
              <div className="lp2-case-panel lp2-case-panel-dark">
                <h3>{t('landingPage.caseFinancialInsights')}</h3>
                <div className="lp2-case-kpi">
                  <div className="lp2-mini-kpi">
                    <strong>{omzetM ? (selectedOmzet >= 100 ? "€100M+" : `~€${selectedOmzet}M`) : "—"}</strong>
                    <span>{t('landingPage.caseProductionScale')}</span>
                  </div>
                  <div className="lp2-mini-kpi">
                    <strong>{cfg && omzetM ? fmtRange(simTotalLow, simTotalHigh) : "—"}</strong>
                    <span>{cfg && omzetM ? t('landingPage.caseLossRange') : t('landingPage.caseLossRangeShort')}</span>
                  </div>
                  <div className="lp2-mini-kpi">
                    <strong>{t('landingPage.caseScenarioAnalysis')}</strong>
                    <span>{t('landingPage.caseScenarioDesc')}</span>
                  </div>
                </div>
                {cfg && omzetM && (
                  <p style={{ marginTop: 16, fontSize: 12, opacity: 0.7, lineHeight: 1.5 }}>{cfg.note}</p>
                )}
              </div>

              <div className="lp2-case-panel">
                <h3>{t('landingPage.caseLossTableTitle')}</h3>
                <table className="lp2-loss-table">
                  <thead>
                    <tr>
                      <th>{t('landingPage.caseLossColPost')}</th>
                      <th>{t('landingPage.caseLossColRange')} <span title={t('landingPage.caseLossColRangeTooltip')} style={{ cursor: "help", color: "#657084", fontSize: 11 }}>&#9432;</span></th>
                      <th>{t('landingPage.caseLossColContext')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cfg && omzetM ? (
                      Object.entries(cfg.verlies).map(([key, v]) => {
                        const isSelected = selectedVerlies.has(key);
                        return (
                          <tr key={key} style={isSelected ? { background: "#fffbf5" } : {}}>
                            <td>
                              {v.label}
                              {isSelected && (
                                <span style={{ fontSize: 10, background: "#f28c1b", color: "white", borderRadius: 999, padding: "2px 7px", fontWeight: 700, verticalAlign: "middle", marginLeft: 6 }}>
                                  {t('landingPage.caseSelected')}
                                </span>
                              )}
                            </td>
                            <td><strong>{fmtRange(omzetM * v.low, omzetM * v.high)}</strong></td>
                            <td style={{ fontSize: 12, color: "#657084" }}>{v.context}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={3} style={{ color: "#657084", fontSize: 13, padding: "16px 0" }}>
                          {t('landingPage.caseEmptyRow')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <p style={{ fontSize: 11, color: "#657084", marginTop: 12, lineHeight: 1.5 }}>
                  {t('landingPage.caseTableNote')}
                </p>
                <details id="how-we-calculate" className="lp2-method-details">
                  <summary className="lp2-method-summary">{t('landingPage.caseMethodSummary')}</summary>
                  <div className="lp2-method-body">
                    <p><strong>{t('landingPage.caseMethodP1Bold')}</strong>{t('landingPage.caseMethodP1Rest')}</p>
                    <p>{t('landingPage.caseMethodP2')}</p>
                    <ul>
                      <li><strong>{t('landingPage.caseMethodDirectCostBold')}</strong>{t('landingPage.caseMethodDirectCostDesc')}</li>
                      <li><strong>{t('landingPage.caseMethodOpCostBold')}</strong>{t('landingPage.caseMethodOpCostDesc')}</li>
                    </ul>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        <section id="partner" style={{ padding: "82px 0", background: "#f6f8fb" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>{t('landingPage.partnerTitle')}</h2>
              <p>{t('landingPage.partnerSubtitle')}</p>
            </div>
            <div className="lp2-split">
              <div className="lp2-info-card">
                <div className="lp2-logo-word"><img src={darkLogo} alt="Taplr" style={{ height: 48, width: "auto" }} /></div>
                <h3>{t('landingPage.taplrProductTitle')}</h3>
                <ul className="lp2-bullets">
                  <li>{t('landingPage.taplrBullet1')}</li>
                  <li>{t('landingPage.taplrBullet2')}</li>
                  <li>{t('landingPage.taplrBullet3')}</li>
                  <li>{t('landingPage.taplrBullet4')}</li>
                </ul>
              </div>
              <div className="lp2-info-card">
                <div className="lp2-logo-word"><img src={flagstoneLogo} alt="Flagstone" style={{ height: 48, width: "auto" }} /></div>
                <h3>{t('landingPage.flagstoneTitle')}</h3>
                <ul className="lp2-bullets">
                  <li>{t('landingPage.flagstoneBullet1')}</li>
                  <li>{t('landingPage.flagstoneBullet2')}</li>
                  <li>{t('landingPage.flagstoneBullet3')}</li>
                  <li>{t('landingPage.flagstoneBullet4')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="personas" style={{ padding: "82px 0" }}>
          <div className="lp2-container">
            <div className="lp2-section-title">
              <h2>{t('landingPage.personasTitle')}</h2>
              <p>{t('landingPage.personasSubtitle')}</p>
            </div>
            <div className="lp2-persona-grid">
              <div className="lp2-persona-card">
                <div className="lp2-persona-role">{t('landingPage.personaOpsRole')}</div>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, letterSpacing: "-0.02em" }}>{t('landingPage.personaOpsTitle')}</h3>
                <p style={{ margin: 0, fontSize: 14, color: "#657084", lineHeight: 1.6 }}>{t('landingPage.personaOpsDesc')}</p>
              </div>
              <div className="lp2-persona-card">
                <div className="lp2-persona-role">{t('landingPage.personaCFORole')}</div>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, letterSpacing: "-0.02em" }}>{t('landingPage.personaCFOTitle')}</h3>
                <p style={{ margin: 0, fontSize: 14, color: "#657084", lineHeight: 1.6 }}>{t('landingPage.personaCFODesc')}</p>
              </div>
              <div className="lp2-persona-card">
                <div className="lp2-persona-role">{t('landingPage.personaITRole')}</div>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, letterSpacing: "-0.02em" }}>{t('landingPage.personaITTitle')}</h3>
                <p style={{ margin: 0, fontSize: 14, color: "#657084", lineHeight: 1.6 }}>{t('landingPage.personaITDesc')}</p>
              </div>
            </div>
          </div>
        </section>

        <div className="lp2-quote-band">
          <div className="lp2-container lp2-quote">
            <div className="lp2-quote-icon">&ldquo;</div>
            <p style={{ fontSize: "clamp(26px, 3vw, 40px)", lineHeight: 1.18, letterSpacing: "-0.035em", margin: 0, fontWeight: 800 }}>
              {t('landingPage.quoteText')}
            </p>
          </div>
        </div>
      </main>

      <footer id="contact" className="lp2-footer">
        <div className="lp2-container lp2-footer-grid">
          <div>
            <h2 style={{ margin: "0 0 12px", fontSize: 40, letterSpacing: "-0.03em" }}>
              {t('landingPage.footerTitle')}
            </h2>
            <p style={{ color: "#b9c3d4" }}>
              {t('landingPage.footerSubtitle')}
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <a className="lp2-cta lp2-cta-secondary" href="mailto:info@flagstone.tech?subject=Kennismaking Taplr">
              {t('landingPage.footerCtaExpert')}
            </a>
            <a className="lp2-cta" href="mailto:info@flagstone.tech?subject=Demo aanvraag">
              {t('landingPage.footerCtaDemo')}
            </a>
          </div>
        </div>
        <div className="lp2-footer-lang">
          <div className="lp2-container">
            <div className="lp2-lang-selector">
              {[
                { code: 'nl', label: 'NL' },
                { code: 'en', label: 'EN' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  className={`lp2-lang-btn${i18n.resolvedLanguage === lang.code ? ' active' : ''}`}
                  onClick={() => i18n.changeLanguage(lang.code)}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
